import { DOMAIN_CONFIG } from '@/integrations/supabase/config';

const BLOCKED_PROTOCOLS = ['javascript:', 'data:', 'blob:'];

/**
 * Validates that a returnTo URL is safe to redirect to.
 * Only allows *.world-class.design domains (and localhost in dev).
 */
export function isValidReturnToUrl(url: string): boolean {
  if (!url) return false;

  try {
    const parsed = new URL(url, window.location.origin);

    if (BLOCKED_PROTOCOLS.includes(parsed.protocol)) {
      return false;
    }

    // Relative paths (resolved against current origin) are always valid
    if (!url.includes('://') && url.startsWith('/')) {
      return true;
    }

    const hostname = parsed.hostname;
    const allowedParent = DOMAIN_CONFIG.parentDomain?.replace(/^\./, '') || 'world-class.design';

    // Exact match or subdomain match
    if (hostname === allowedParent || hostname.endsWith(`.${allowedParent}`)) {
      return true;
    }

    // In dev, also allow localhost
    if (import.meta.env.DEV) {
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return true;
      }
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Resolves a returnTo URL into a redirect target.
 * Returns whether the redirect is external (needs window.location.href)
 * or internal (can use navigate()).
 */
export function resolveReturnToRedirect(returnTo: string): { url: string; isExternal: boolean } {
  try {
    const parsed = new URL(returnTo, window.location.origin);

    if (parsed.hostname === window.location.hostname) {
      return { url: parsed.pathname + parsed.search + parsed.hash, isExternal: false };
    }

    return { url: parsed.href, isExternal: true };
  } catch {
    return { url: returnTo, isExternal: false };
  }
}
